<script setup>
import { computed } from 'vue';
import XlCard from '../xl/XlCard.vue';
import XlAvatar from '../xl/XlAvatar.vue';
import { plural } from '../../helpers/plural';
import { describeRule, policyTypeList } from './segmentRuleHelpers';

/**
 * What the rule being written will actually reach.
 *
 * A count on its own is not checkable — 412 looks identical whether the rule is
 * right or wrong — so it is shown next to the rule restated in plain words and
 * a few of the people it names. One obviously-wrong name is what makes somebody
 * look again before saving.
 */
const props = defineProps({
  rule: { type: Object, default: null },
  estimate: { type: Object, default: null },
  valid: { type: Boolean, default: true },
});

const known = computed(() => props.valid
  && props.estimate
  && props.estimate.policyholders !== null
  && props.estimate.policyholders !== undefined);

const noneChosen = computed(() => props.rule?.definition_type === 'policy_type'
  && policyTypeList(props.rule.policy_type).length === 0);

// A broker's own share is the number that means something to them; the agency
// figure would read as their own reach.
const headline = computed(() => {
  if (noneChosen.value) return '0';
  if (!known.value) return null;
  const { policyholders, mine } = props.estimate;
  return Number(mine ?? policyholders).toLocaleString('en-US');
});

const unit = computed(() => plural(headline.value, 'policyholder').split(' ').pop());

const agencyWide = computed(() => {
  if (!known.value) return null;
  const { policyholders, mine } = props.estimate;
  if (mine === null || mine === undefined) return null;
  return `${Number(policyholders).toLocaleString('en-US')} agency-wide`;
});

const words = computed(() => describeRule(props.rule));

const sample = computed(() => (known.value ? props.estimate.sample || [] : []));

const renewalLine = (days) => {
  if (days === null || days === undefined) return 'No upcoming renewal';
  if (days < 0) return 'Renewal passed';
  if (days === 0) return 'Renews today';
  return `Renews in ${days} ${days === 1 ? 'day' : 'days'}`;
};
</script>

<template>
  <XlCard padded class="audience" data-testid="segment-audience">
    <b class="audience__heading">Who this includes</b>

    <template v-if="headline !== null">
      <div class="audience__count" data-testid="segment-audience-count">{{ headline }}</div>
      <div class="audience__unit">{{ unit }}, updated live</div>
      <div v-if="agencyWide" class="audience__unit" data-testid="segment-audience-agency">{{ agencyWide }}</div>
    </template>
    <p v-else class="audience__pending" data-testid="segment-audience-pending">
      {{ props.valid ? 'Working out who this reaches…' : 'Finish the rule to see who it reaches.' }}
    </p>

    <div class="audience__section">
      <b class="audience__sub">In plain words</b>
      <p class="audience__words" data-testid="segment-audience-words">{{ words }}</p>
    </div>

    <div v-if="sample.length" class="audience__section">
      <b class="audience__sub">A sample</b>
      <ul class="audience__sample" data-testid="segment-audience-sample">
        <li v-for="person in sample" :key="person.name" class="audience__person">
          <XlAvatar class="audience__avatar" :name="person.name" :color="person.avatar_color || ''" :size="30" />
          <span class="audience__who">
            <b>{{ person.name }}</b>
            <span>{{ renewalLine(person.renews_in_days) }}</span>
          </span>
        </li>
      </ul>
    </div>
  </XlCard>
</template>

<style scoped lang="scss">
.audience {
  font-size: 16px;
  line-height: normal;
  color: var(--xl-color-primary-text);

  &__heading { font-size: 15px; }

  &__count {
    margin: 12px 0 2px;
    font-size: 34px;
    font-weight: 600;
    letter-spacing: -0.03em;
  }

  &__unit { font-size: 13px; color: var(--xl-color-outline); }

  &__pending { margin: 12px 0 0; font-size: 13px; color: var(--xl-color-outline); }

  &__section { margin-top: 18px; }

  &__sub { font-size: 13.5px; }

  &__words {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.55;
    color: var(--xl-color-secondary-text);
  }

  &__sample { margin: 10px 0 0; padding: 0; list-style: none; }

  &__person {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 8px 0;
    border-bottom: 1px solid var(--xl-color-border-subtle);

    &:last-child { border-bottom: 0; }
  }

  // XlAvatar writes its font-size inline; only !important outranks that.
  &__avatar { font-size: 11px !important; }

  &__who {
    flex: 1;
    min-width: 0;

    b { display: block; font-size: 13.5px; }
    span { font-size: 12px; color: var(--xl-color-outline); }
  }
}
</style>
